import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { ILeadNowConvMonthlyHistory, LeadNowConvVM } from 'app/stores/ui/agt.leadnow.conv.ui.store';
import { formatNum, formatPercentWithIcon } from 'app/utils';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';

// Table row: one month of the funnel, plus the quote derived from the sums.
interface IConvMonthlyRow extends ILeadNowConvMonthlyHistory {
	conversionRate: number;
}

/**
 * Monthly development of the Lead-Conversion funnel. Shows the four funnel
 * stages (same colors as LeadNowConversionFunnelChart) as grouped bars per
 * month, with the Abschlussquote as a line on the right axis.
 */
export const LeadNowConversionHistory = observer(() => {
	const { berichteUiiStore, agtLeadNowConvUiStore, agenturListUiStore } = useStore();
	const [agtIds, setAgtIds] = useState<number[]>([]);

	// The monthly numbers behind the chart, one row per month.
	const tm = useMemo(() => {
		const tm = new TableModel<IConvMonthlyRow, number>();
		const cols: ITableColumn<IConvMonthlyRow, number>[] = [
			{
				label: 'Monat',
				path: 'bm',
				sortBy: 'bmRaw',
				sortType: 'number',
			},
			{
				label: 'Brutto',
				path: 'brutto',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Netto',
				path: 'netto',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Konvertiert',
				path: 'converted',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Abschlussquote',
				path: 'conversionRate',
				format: (val: number) => formatPercentWithIcon(val, 1),
				agg: [
					{
						aggType: AggregrationType.avg,
						format: (val: number) => formatPercentWithIcon(val, 1),
					},
				],
			},
			{
				label: 'Neu Stück',
				path: 'neuStck',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Neu Bewertung',
				path: 'neuBew',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
		];

		tm.sortBy = 'bmRaw';
		tm.setCols(cols);
		tm.idProperty = 'bmRaw';
		tm.idType = 'number';
		return tm;
	}, []);

	const chartConfig: ITimeSeriesChartConfig<ILeadNowConvMonthlyHistory> = useMemo(
		() => ({
			title: 'Lead-Conversion Entwicklung (monatlich)',
			tag: 'AMIS.NOW Leads',
			tagColor: 'turquoise',
			height: 400,

			xAxisField: 'bm',
			xAxisRotation: 45,

			yAxes: [
				{
					name: 'Anzahl',
					position: 'left',
				},
				{
					name: 'Abschlussquote (%)',
					position: 'right',
					formatter: (v) => `${v}%`,
					valueType: 'percentage',
					splitLine: false,
				},
			],

			derivedFields: [
				{
					fieldName: 'abschlussquote',
					calculate: (d) => (d.netto > 0 ? (d.converted / d.netto) * 100 : 0),
				},
			],

			series: [
				{
					name: 'Brutto Leads',
					dataField: 'brutto',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.purple,
					barMaxWidth: 24,
				},
				{
					name: 'Netto Leads',
					dataField: 'netto',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.leadnowNetto,
					barMaxWidth: 24,
				},
				{
					name: 'Konvertierte Leads',
					dataField: 'converted',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.turquoise,
					barMaxWidth: 24,
				},
				{
					name: 'Stücke',
					dataField: 'neuStck',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.blue,
					barMaxWidth: 24,
				},
				{
					name: 'Abschlussquote (%)',
					dataField: 'abschlussquote',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.black,
					symbolSize: 6,
					lineStyle: { width: 3 },
				},
			],

			dataLoader: async (ids) => agtLeadNowConvUiStore.loadConvHistoryByMonth(ids),
		}),
		[agtLeadNowConvUiStore],
	);

	useEffect(() => {
		agtLeadNowConvUiStore.load();
	}, [agtLeadNowConvUiStore]);

	// The chart always spans all available kws; the kw range picker of the
	// Übersicht does not apply here.
	useEffect(() => {
		const kws = agtLeadNowConvUiStore.kws;
		if (kws.length > 0) {
			agtLeadNowConvUiStore.aggregateAgtsByKw(kws[0], kws[kws.length - 1]);
		}
	}, [agtLeadNowConvUiStore, agtLeadNowConvUiStore.kws]);

	// Same agt scoping as LeadNowConversionListe: a single selected agentur, or
	// the filtered gp-relevant list.
	useEffect(() => {
		let items = agtLeadNowConvUiStore.items;
		if (items.length === 0) {
			setAgtIds([]);
			return;
		}

		if (berichteUiiStore.selectedAgt) {
			const agtId = berichteUiiStore.selectedAgt.agtId;
			items = items.filter((i) => i.agt.agtId === agtId);
		} else {
			items = agenturListUiStore.applyFilterOnAgt(items) as LeadNowConvVM[];
			items = agenturListUiStore.applyFilterOnGpRelevantAgt(items) as LeadNowConvVM[];
		}
		setAgtIds(items.map((i) => i.agtId));
	}, [berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtLeadNowConvUiStore.items]);

	// Same (cached) monthly data as the chart, as table rows.
	useEffect(() => {
		if (agtIds.length === 0) {
			tm.setRowData([]);
			return;
		}
		let cancelled = false;
		agtLeadNowConvUiStore.loadConvHistoryByMonth(agtIds).then((months) => {
			if (cancelled) {
				return;
			}
			const rows: IConvMonthlyRow[] = months.map((m) => ({
				...m,
				conversionRate: m.netto > 0 ? (m.converted / m.netto) * 100 : 0,
			}));
			tm.setRowData(rows);
			tm.sort();
		});
		return () => {
			cancelled = true;
		};
	}, [tm, agtLeadNowConvUiStore, agtIds]);

	return (
		<div className="table-container">
			<div className="pad-1rem">
				{agtIds.length === 0 ? <div>Keine Lead-Conversion-Daten</div> : <TimeSeriesChart config={chartConfig} agtIds={agtIds} />}
			</div>
			{agtIds.length > 0 && <Table stickyHeader={true} tm={tm} stickyFooter={true} />}
		</div>
	);
});
